import type { ThemeConfig, Topic } from "@app/core";
import { topicColorResolved } from "@app/core";
import { useMemo } from "react";
import { orderByIds } from "../../lib/apply-order.js";
import { useThemeStore } from "../../stores/theme.store.js";
import { useSettingsStore, useTopicsStore } from "../../stores/index.js";

/** A topic normalized for selection UIs: its THEME-resolved color (override or
 *  categorical-palette default by stable global index — the same source the
 *  calendar blocks use), plus its icon. */
export interface TopicNode {
  id: string;
  name: string;
  /** Theme-resolved color (hex), always set. */
  color: string;
  icon: string | null;
}

export interface TopicRootNode extends TopicNode {
  children: TopicNode[];
}

/** Pure: root topics (in the user's order) each with their direct children (in
 *  the user's per-parent order), carrying THEME-resolved colors. Color resolves
 *  to `theme.entity.topics[id]` or the categorical palette by the topic's GLOBAL
 *  index (position in `topics`) — identical to `useTaskContextColor` /
 *  `CalendarRenderIndex`, so the create form and the calendar never diverge.
 *  Shared by the desktop paper rail (`useTopicRail`) and the mobile topic buttons. */
export function buildTopicRail(
  topics: Topic[],
  topicOrder: string[],
  topicChildOrder: Record<string, string[]>,
  theme: ThemeConfig,
): TopicRootNode[] {
  const indexById = new Map(topics.map((t, i) => [t.id, i]));
  const colorOf = (id: string) => topicColorResolved(theme, id, indexById.get(id) ?? 0);
  const roots = orderByIds(topics.filter((t) => !t.parent_id), topicOrder, (t) => t.id);
  return roots.map((r) => ({
    id: r.id,
    name: r.name,
    color: colorOf(r.id),
    icon: r.icon,
    children: orderByIds(
      topics.filter((t) => t.parent_id === r.id),
      topicChildOrder[r.id] ?? [],
      (t) => t.id,
    ).map((c) => ({
      id: c.id,
      name: c.name,
      color: colorOf(c.id),
      icon: c.icon,
    })),
  }));
}

/** Reactive `buildTopicRail` bound to the topics + order + theme stores. */
export function useTopicRailModel(): TopicRootNode[] {
  const topics = useTopicsStore((s) => s.topics);
  const topicOrder = useSettingsStore((s) => s.topicOrder);
  const topicChildOrder = useSettingsStore((s) => s.topicChildOrder);
  const theme = useThemeStore((s) => s.config);
  return useMemo(
    () => buildTopicRail(topics, topicOrder, topicChildOrder, theme),
    [topics, topicOrder, topicChildOrder, theme],
  );
}
